import { ReactNode } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { Edit } from '@mui/icons-material';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { Address } from '../../interfaces';

export function formatPhone(phone?: string | null): string {
  if (!phone) return '';
  const parsed = parsePhoneNumberFromString(phone, 'NO');
  // Show anything that isn't a valid number as typed, rather than dressing it up as one.
  if (!parsed?.isValid()) return phone;
  // libphonenumber groups Norwegian mobile numbers 2-2-2-2; the usual way to write them is 3-2-3.
  const national = parsed.nationalNumber;
  if (parsed.countryCallingCode === '47' && /^[49]\d{7}$/.test(national)) {
    const extension = parsed.ext ? ` ext. ${parsed.ext}` : '';
    return `+47 ${national.slice(0, 3)} ${national.slice(3, 5)} ${national.slice(5)}${extension}`;
  }
  return parsed.formatInternational();
}

export function formatAddress(address?: Address | null): string {
  if (!address) return '';
  const parts: string[] = [];
  if (address.address1) parts.push(address.address1);
  const cityLine = [address.postalCode, address.city].filter(Boolean).join(' ').trim();
  if (cityLine) parts.push(cityLine);
  return parts.join(', ');
}

export interface InfoRowProps {
  icon: ReactNode;
  label: string;
  value?: string | null;
  valueRender?: (v: string) => ReactNode;
  onEdit?: () => void;
  hover?: boolean;
}

export function InfoRow({ icon, label, value, valueRender, onEdit, hover = true }: InfoRowProps) {
  const display = value ?? '';
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 1.5,
        py: 1.25,
        borderRadius: 1.5,
        transition: (theme) => theme.transitions.create('background-color', { duration: theme.transitions.duration.shortest }),
        '&:hover': hover ? { bgcolor: 'action.hover' } : undefined,
        '&:hover .info-row-edit': { opacity: 1 },
      }}>
      <Box sx={{ display: 'inline-flex', color: 'text.secondary', '& > *': { fontSize: 'medium' } }}>{icon}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="overline" component="div" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {label}
        </Typography>
        <Typography variant="body2" component="div" sx={{ wordBreak: 'break-word' }}>
          {valueRender && display ? valueRender(display) : display || '–'}
        </Typography>
      </Box>
      {onEdit && (
        <IconButton
          className="info-row-edit"
          size="small"
          onClick={onEdit}
          sx={{
            opacity: 0,
            transition: (theme) => theme.transitions.create('opacity', { duration: theme.transitions.duration.shortest }),
            '&:focus-visible': { opacity: 1 },
          }}>
          <Edit fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
}
